import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'
import { useTranslation } from '@/stores/i18nStore'
import { useLookupWord, useDictionarySearch } from '@/hooks/useDictionary'
import { useAnalytics } from '@/hooks/useAnalytics'
import { SearchIcon, BookIcon, LoaderIcon } from '@/components/ui/icons'
import type { DictionaryEntry } from '@/types/dictionary'

type DictionarySearchMode = 'literal' | 'position' | 'form'

interface PositionOptions {
  skip: number[]
  repeat: number[]
  exact: number[]
  derivate: boolean
}

// Tenshuashi characters for derivate option
const TENSHUASHI_CHARS = ['ለ', 'ሉ', 'ሊ', 'ላ', 'ሌ', 'ል', 'ሎ', 'ሏ',
                          'ሰ', 'ሱ', 'ሲ', 'ሳ', 'ሴ', 'ስ', 'ሶ', 'ሷ',
                          'ተ', 'ቱ', 'ቲ', 'ታ', 'ቴ', 'ት', 'ቶ', 'ቷ',
                          'ነ', 'ኑ', 'ኒ', 'ና', 'ኔ', 'ን', 'ኖ', 'ኗ',
                          'ደ', 'ዱ', 'ዲ', 'ዳ', 'ዴ', 'ድ', 'ዶ', 'ዷ',
                          'ጀ', 'ጁ', 'ጂ', 'ጃ', 'ጄ', 'ጅ', 'ጆ', 'ጇ',
                          'ገ', 'ጉ', 'ጊ', 'ጋ', 'ጌ', 'ግ', 'ጎ', 'ጏ',
                          'ጠ', 'ጡ', 'ጢ', 'ጣ', 'ጤ', 'ጥ', 'ጦ', 'ጧ',
                          'ጸ', 'ጹ', 'ጺ', 'ጻ', 'ጼ', 'ጽ', 'ጾ', 'ጿ',
                          'ፈ', 'ፉ', 'ፊ', 'ፋ', 'ፌ', 'ፍ', 'ፎ', 'ፏ']

export function DictionaryPage() {
  const { t } = useTranslation()
  const { trackDictionaryLookup, trackDictionarySearch, trackSynonymClick, trackRelatedWordClick } = useAnalytics()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedEntry, setSelectedEntry] = useState<DictionaryEntry | null>(null)
  const [recentLookups, setRecentLookups] = useState<DictionaryEntry[]>([])

  // Search mode state
  const [searchMode, setSearchMode] = useState<DictionarySearchMode>('literal')
  const [matchTypes, setMatchTypes] = useState<Array<'exact' | 'prefix' | 'suffix' | 'contains'>>(['exact'])
  const [positionOptions, setPositionOptions] = useState<PositionOptions>({
    skip: [],
    repeat: [],
    exact: [],
    derivate: false
  })
  const [formPattern, setFormPattern] = useState('')

  // Reset position options when search query changes
  useEffect(() => {
    setPositionOptions({ skip: [], repeat: [], exact: [], derivate: false })
  }, [searchQuery])

  // Position option toggles
  const togglePositionSkip = (index: number) => {
    setPositionOptions(prev => ({
      ...prev,
      skip: prev.skip.includes(index)
        ? prev.skip.filter(i => i !== index)
        : [...prev.skip, index],
      repeat: prev.repeat.filter(i => i !== index) // Can't be both skip and repeat
    }))
  }

  const togglePositionRepeat = (index: number) => {
    setPositionOptions(prev => ({
      ...prev,
      repeat: prev.repeat.includes(index)
        ? prev.repeat.filter(i => i !== index)
        : [...prev.repeat, index],
      skip: prev.skip.filter(i => i !== index) // Can't be both skip and repeat
    }))
  }

  const togglePositionExact = (index: number) => {
    setPositionOptions(prev => ({
      ...prev,
      exact: prev.exact.includes(index)
        ? prev.exact.filter(i => i !== index)
        : [...prev.exact, index]
    }))
  }

  const togglePositionDerivate = () => {
    setPositionOptions(prev => ({ ...prev, derivate: !prev.derivate }))
  }

  // Toggle match type (multi-select)
  const toggleMatchType = (type: 'exact' | 'prefix' | 'suffix' | 'contains') => {
    setMatchTypes(prev => {
      if (prev.includes(type)) {
        // Don't allow deselecting the last one
        if (prev.length === 1) return prev
        return prev.filter(t => t !== type)
      }
      return [...prev, type]
    })
  }

  const lookupMutation = useLookupWord()
  const { data: searchResults, isLoading: searchLoading } = useDictionarySearch(
    searchQuery.length >= 2 ? searchQuery : '',
    20,
    {
      searchMode,
      matchTypes,
      positionOptions: searchMode === 'position' ? positionOptions : undefined,
      formPattern: searchMode === 'form' ? formPattern : undefined
    }
  )

  // Track search results
  useEffect(() => {
    if (searchResults && searchQuery.length >= 2) {
      trackDictionarySearch(searchQuery, searchResults.results.length)
    }
  }, [searchResults, searchQuery, trackDictionarySearch])

  const handleLookup = useCallback(async (word: string, _source?: 'search' | 'synonym' | 'related') => {
    try {
      const result = await lookupMutation.mutateAsync(word)
      const found = result && result.found && result.results.length > 0
      trackDictionaryLookup(word, found, result?.results?.length || 0)

      if (found) {
        const entry = result.results[0]
        setSelectedEntry(entry)
        // Add to recent lookups (avoid duplicates)
        setRecentLookups(prev => {
          const filtered = prev.filter(e => e.word !== entry.word)
          return [entry, ...filtered].slice(0, 10)
        })
      }
    } catch (error) {
      console.error('Lookup failed:', error)
    }
  }, [lookupMutation, trackDictionaryLookup])

  const handleSynonymClick = useCallback((originalWord: string, synonym: string) => {
    trackSynonymClick(originalWord, synonym)
    handleLookup(synonym, 'synonym')
  }, [handleLookup, trackSynonymClick])

  const handleRelatedWordClick = useCallback((originalWord: string, relatedWord: string) => {
    trackRelatedWordClick(originalWord, relatedWord)
    handleLookup(relatedWord, 'related')
  }, [handleLookup, trackRelatedWordClick])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim().length >= 2) {
      handleLookup(searchQuery.trim())
    }
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-zinc-950">
      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-zinc-900 dark:bg-white flex items-center justify-center">
            <BookIcon className="w-5 h-5 text-white dark:text-zinc-900" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">
              {t('dictionary')}
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {t('dictionaryDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Search panel */}
        <div className="w-96 border-r border-zinc-200 dark:border-zinc-800 flex flex-col">
          {/* Search input */}
          <form onSubmit={handleSearchSubmit} className="p-4 space-y-3">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('searchDictionary')}
                className={cn(
                  'w-full pl-10 pr-4 py-3 rounded-xl',
                  'bg-zinc-100 dark:bg-zinc-900',
                  'border border-transparent',
                  'focus:border-zinc-300 dark:focus:border-zinc-700',
                  'focus:outline-none focus:ring-2 focus:ring-zinc-200 dark:focus:ring-zinc-800',
                  'text-zinc-900 dark:text-white',
                  'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
                  'font-ethiopic'
                )}
              />
            </div>

            {/* Search Mode Selector */}
            <div className="flex gap-1 p-1 bg-zinc-100 dark:bg-zinc-900 rounded-lg">
              {(['literal', 'position', 'form'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSearchMode(mode)}
                  className={cn(
                    'flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-all',
                    searchMode === mode
                      ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm'
                      : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                  )}
                >
                  {t(mode)}
                </button>
              ))}
            </div>

            {/* Match Type Selector - Ghost multi-select buttons */}
            <div className="flex gap-1.5 flex-wrap">
              {(['exact', 'prefix', 'suffix', 'contains'] as const).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleMatchType(type)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-all border',
                    matchTypes.includes(type)
                      ? 'bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 border-zinc-900 dark:border-white'
                      : 'bg-transparent text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700 hover:border-zinc-400 dark:hover:border-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                  )}
                >
                  {t(type)}
                </button>
              ))}
            </div>

            {/* Position Options */}
            {searchMode === 'position' && searchQuery.length > 0 && (
              <PositionOptionsPanel
                searchString={searchQuery}
                positionOptions={positionOptions}
                onToggleSkip={togglePositionSkip}
                onToggleRepeat={togglePositionRepeat}
                onToggleExact={togglePositionExact}
                onToggleDerivate={togglePositionDerivate}
              />
            )}

            {/* Form Pattern Input */}
            {searchMode === 'form' && (
              <div className="space-y-1">
                <label className="text-xs text-zinc-500 dark:text-zinc-400">
                  {t('formPattern')} {t('formPatternHint')}
                </label>
                <input
                  type="text"
                  value={formPattern}
                  onChange={(e) => setFormPattern(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="e.g., 123"
                  className={cn(
                    'w-full px-3 py-2 rounded-lg',
                    'bg-zinc-100 dark:bg-zinc-900',
                    'border border-zinc-200 dark:border-zinc-800',
                    'focus:border-zinc-300 dark:focus:border-zinc-700',
                    'focus:outline-none',
                    'text-zinc-900 dark:text-white text-sm font-mono'
                  )}
                />
              </div>
            )}
          </form>

          {/* Search results */}
          <div className="flex-1 overflow-y-auto">
            {searchLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoaderIcon className="w-6 h-6 text-zinc-400" />
              </div>
            ) : searchResults && searchResults.results.length > 0 ? (
              <div className="px-2 pb-4">
                <p className="px-2 py-1 text-xs text-zinc-400">
                  {searchResults.results.length} {t('results')}
                </p>
                {searchResults.results.map((entry, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleLookup(entry.word)}
                    className={cn(
                      'w-full px-3 py-2.5 rounded-lg text-left transition-colors',
                      selectedEntry?.word === entry.word
                        ? 'bg-zinc-100 dark:bg-zinc-800'
                        : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'
                    )}
                  >
                    <p className="font-medium text-zinc-900 dark:text-white font-ethiopic">
                      {entry.word}
                    </p>
                    {entry.type && (
                      <p className="text-xs text-zinc-400 mt-0.5">
                        {entry.type}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            ) : searchQuery.length >= 2 ? (
              <div className="text-center py-8 px-4">
                <p className="text-sm text-zinc-400">{t('noResultsFound')}</p>
              </div>
            ) : recentLookups.length > 0 ? (
              <div className="px-2 pb-4">
                <p className="px-2 py-1 text-xs text-zinc-400 uppercase tracking-wide">
                  {t('recentLookups')}
                </p>
                {recentLookups.map((entry, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedEntry(entry)}
                    className={cn(
                      'w-full px-3 py-2.5 rounded-lg text-left transition-colors',
                      selectedEntry?.word === entry.word
                        ? 'bg-zinc-100 dark:bg-zinc-800'
                        : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'
                    )}
                  >
                    <p className="font-medium text-zinc-900 dark:text-white font-ethiopic">
                      {entry.word}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 px-4">
                <BookIcon className="w-12 h-12 mx-auto text-zinc-200 dark:text-zinc-700 mb-3" />
                <p className="text-sm text-zinc-400">{t('startSearchingDictionary')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Entry detail panel */}
        <div className="flex-1 overflow-y-auto p-6">
          <AnimatePresence mode="wait">
            {lookupMutation.isPending ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center h-full"
              >
                <LoaderIcon className="w-8 h-8 text-zinc-400" />
              </motion.div>
            ) : selectedEntry ? (
              <motion.div
                key={selectedEntry.word}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="max-w-2xl"
              >
                {/* Word */}
                <div className="mb-6">
                  <h2 className="text-4xl font-bold text-zinc-900 dark:text-white font-ethiopic mb-2">
                    {selectedEntry.word}
                  </h2>
                  {selectedEntry.type && (
                    <span className="inline-block px-2 py-1 rounded-md bg-zinc-100 dark:bg-zinc-800 text-sm text-zinc-600 dark:text-zinc-400">
                      {selectedEntry.type}
                    </span>
                  )}
                </div>

                {/* Definition */}
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-2">
                    {t('definition')}
                  </h3>
                  <p className="text-lg text-zinc-700 dark:text-zinc-300 font-ethiopic leading-relaxed">
                    {selectedEntry.definition}
                  </p>
                </div>

                {/* Synonyms */}
                {selectedEntry.synonyms && selectedEntry.synonyms.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-2">
                      {t('synonyms')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedEntry.synonyms.map((synonym, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSynonymClick(selectedEntry.word, synonym)}
                          className="px-3 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 text-sm font-ethiopic hover:bg-emerald-100 dark:hover:bg-emerald-900 transition-colors"
                        >
                          {synonym}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related words */}
                {selectedEntry.relatedWords && selectedEntry.relatedWords.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-2">
                      {t('relatedWords')}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedEntry.relatedWords.map((word, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleRelatedWordClick(selectedEntry.word, word)}
                          className="px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 text-sm font-ethiopic hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors"
                        >
                          {word}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center h-full text-center"
              >
                <div className="w-24 h-24 rounded-2xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mb-4">
                  <BookIcon className="w-12 h-12 text-zinc-300 dark:text-zinc-600" />
                </div>
                <h3 className="text-xl font-medium text-zinc-600 dark:text-zinc-400 mb-2">
                  {t('selectWordToView')}
                </h3>
                <p className="text-sm text-zinc-400 max-w-sm">
                  {t('dictionaryHint')}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

// Position Options Panel Component
interface PositionOptionsPanelProps {
  searchString: string
  positionOptions: PositionOptions
  onToggleSkip: (index: number) => void
  onToggleRepeat: (index: number) => void
  onToggleExact: (index: number) => void
  onToggleDerivate: () => void
}

function PositionOptionsPanel({
  searchString,
  positionOptions,
  onToggleSkip,
  onToggleRepeat,
  onToggleExact,
  onToggleDerivate
}: PositionOptionsPanelProps) {
  const { t } = useTranslation()
  const chars = [...searchString]

  // Check if there's a tenshuashi character (for derivate option)
  const hasTenshuashi = chars.some(char => TENSHUASHI_CHARS.includes(char))

  const skip = positionOptions?.skip || []
  const repeat = positionOptions?.repeat || []
  const exact = positionOptions?.exact || []
  const derivate = positionOptions?.derivate || false

  return (
    <div className="space-y-2 p-2 bg-zinc-50 dark:bg-zinc-900/50 rounded-lg text-xs">
      {/* Skip option */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-zinc-400 w-12">{t('skip')}:</span>
        <div className="flex gap-0.5 flex-wrap">
          {chars.map((char, index) => (
            <button
              key={`skip-${index}`}
              type="button"
              onClick={() => onToggleSkip(index)}
              className={cn(
                'w-6 h-6 rounded text-sm font-ethiopic transition-all',
                skip.includes(index)
                  ? 'bg-amber-500 text-white'
                  : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700'
              )}
              title={t('skipCharacter')}
            >
              {char}
            </button>
          ))}
        </div>
      </div>

      {/* Repeat option */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-zinc-400 w-12">{t('repeat')}:</span>
        <div className="flex gap-0.5 flex-wrap">
          {chars.map((char, index) => (
            <button
              key={`repeat-${index}`}
              type="button"
              onClick={() => onToggleRepeat(index)}
              className={cn(
                'w-6 h-6 rounded text-sm font-ethiopic transition-all',
                repeat.includes(index)
                  ? 'bg-blue-500 text-white'
                  : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700'
              )}
              title={t('repeatCharacter')}
            >
              {char}
            </button>
          ))}
        </div>
      </div>

      {/* Exact option */}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-zinc-400 w-12">{t('exact')}:</span>
        <div className="flex gap-0.5 flex-wrap">
          {chars.map((char, index) => (
            <button
              key={`exact-${index}`}
              type="button"
              onClick={() => onToggleExact(index)}
              className={cn(
                'w-6 h-6 rounded text-sm font-ethiopic transition-all',
                exact.includes(index)
                  ? 'bg-rose-500 text-white'
                  : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700'
              )}
              title={t('exactCharacter')}
            >
              {char}
            </button>
          ))}
        </div>
      </div>

      {/* Derivate option */}
      <div className="flex items-center gap-1">
        <span className={cn(
          'w-12',
          hasTenshuashi ? 'text-zinc-400' : 'text-zinc-300 dark:text-zinc-600'
        )}>{t('derivate')}:</span>
        <button
          type="button"
          onClick={hasTenshuashi ? onToggleDerivate : undefined}
          disabled={!hasTenshuashi}
          className={cn(
            'px-2 py-1 rounded text-sm font-ethiopic transition-all',
            !hasTenshuashi
              ? 'bg-zinc-200 dark:bg-zinc-800 text-zinc-300 dark:text-zinc-600 cursor-not-allowed opacity-50'
              : derivate
                ? 'bg-violet-500 text-white'
                : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-300 dark:hover:bg-zinc-700'
          )}
          title={hasTenshuashi ? t('derivateWord') : 'No tenshuashi character'}
        >
          {searchString}
        </button>
      </div>
    </div>
  )
}
